/* eslint-disable */
// The above comment disables ESLint for this file to suppress source map warnings from react-datepicker
/* eslint-disable */
// The above comment disables ESLint for this file to suppress source map warnings from react-datepicker
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { get_visualization_data } from '../../api/api';
import { usePipeline } from '../../context/PipelineContext';
import { Chart as ChartJS, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DataVisualization.css';

// Make Chart globally available for direct access
const Chart = ChartJS;

// Create a custom plugin to maintain zoom state
const persistentZoomPlugin = {
  id: 'persistentZoom',
  beforeUpdate: function(chart) {
    // If we have a custom zoom state, re-apply it
    if (chart._customZoomState) {
      const { min, max } = chart._customZoomState;
      if (chart.options.scales && chart.options.scales.x) {
        chart.options.scales.x.min = min;
        chart.options.scales.x.max = max;
      }
    }
  }
};

// Register Chart.js components
ChartJS.register(...registerables, zoomPlugin, persistentZoomPlugin);

const DataVisualization = ({ 
  data, 
  selectedColumns, 
  handleColumnSelect, 
  nodeName,
  onSwitchToTable,
  pipelineName
}) => {
  const { session } = usePipeline();
  const [chartError, setChartError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sumValues, setSumValues] = useState({});
  const [visibleSumValues, setVisibleSumValues] = useState({});
  const [visibleDateRange, setVisibleDateRange] = useState({ start: '', end: '' });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateTimeArray, setDateTimeArray] = useState([]);
  const chartRef = useRef(null);
  
  // Function to reset zoom level
  const resetZoom = () => {
    try {
      console.log("Resetting zoom");
      
      // Reset visible sums to total sums when zoom is reset
      setVisibleSumValues({...sumValues});
      
      // Reset date range to full range
      if (chartData && chartData.date_time) {
        setVisibleDateRange({
          start: chartData.date_time[0] || '',
          end: chartData.date_time[chartData.date_time.length - 1] || ''
        });
        
        // Reset date pickers to full range
        if (dateTimeArray.length > 0) {
          setStartDate(dateTimeArray[0]);
          setEndDate(dateTimeArray[dateTimeArray.length - 1]);
        }
      }
      
      // Get the chart canvas and reset zoom
      const chartCanvas = document.querySelector('.chart-wrapper canvas');
      if (chartCanvas) {
        const chartInstance = Chart.getChart(chartCanvas);
        if (chartInstance) {
          // Clear custom zoom state
          if (chartInstance._customZoomState) {
            delete chartInstance._customZoomState;
          }
          
          // Restore original scale configuration if available
          if (chartInstance._originalScaleConfig && chartInstance._originalScaleConfig.x) {
            chartInstance.options.scales.x = { ...chartInstance._originalScaleConfig.x };
          }
          
          // Reset zoom using the built-in method
          chartInstance.resetZoom();
          
          // Remove any fixed min/max values
          if (chartInstance.options.scales && chartInstance.options.scales.x) {
            delete chartInstance.options.scales.x.min;
            delete chartInstance.options.scales.x.max;
            
            // Ensure we still have the tick limit for readability
            chartInstance.options.scales.x.ticks = {
              ...chartInstance.options.scales.x.ticks,
              maxTicksLimit: 25,
              autoSkip: true,
              autoSkipPadding: 10
            };
          }
          
          // Update the chart
          chartInstance.update();
          console.log("Chart zoom reset successfully");
        }
      } else if (chartRef && chartRef.current) {
        // Fallback to using ref
        chartRef.current.resetZoom();
      }
    } catch (error) {
      console.error("Error resetting zoom:", error);
    }
  };
  
  // Function to apply date range filter
  const applyDateRangeFilter = () => {
    if (!chartData || !startDate || !endDate || dateTimeArray.length === 0) {
      console.error("Missing required data for date range filter");
      return;
    }
    
    console.log("Applying date range filter:", { 
      startDate: startDate.toString(), 
      endDate: endDate.toString() 
    });
    
    // Find indices of the selected date range in the dateTimeArray
    let startIndex = -1;
    let endIndex = -1;
    
    // More robust approach to find indices
    for (let i = 0; i < dateTimeArray.length; i++) {
      const currentDate = dateTimeArray[i];
      
      // Find the first date that's greater than or equal to startDate
      if (startIndex === -1 && currentDate >= startDate) {
        startIndex = i;
      }
      
      // Find the first date that's greater than endDate
      if (currentDate > endDate) {
        endIndex = i;
        break;
      }
    }
    
    // If we didn't find an end index, use the last element
    if (endIndex === -1) {
      endIndex = dateTimeArray.length;
    }
    
    const actualEndIndex = endIndex - 1;
    
    console.log("Date indices:", { startIndex, endIndex, actualEndIndex });
    
    if (startIndex === -1 || startIndex > actualEndIndex) {
      console.error("Invalid date range");
      return; // Invalid range
    }
    
    // Update the visible date range display
    setVisibleDateRange({
      start: chartData.date_time[startIndex] || '',
      end: chartData.date_time[actualEndIndex] || ''
    });
    
    // Calculate sums for the selected range
    const newVisibleSums = {};
    Object.keys(chartData).forEach(key => {
      if (key !== 'date_time' && key !== '_zoomStart' && key !== '_zoomEnd') {
        // Check if the data is an array before processing
        if (Array.isArray(chartData[key])) {
          let sum = 0;
          
          // Sum only the data points in the selected range
          for (let i = startIndex; i <= actualEndIndex; i++) {
            if (i < chartData[key].length) {
              sum += parseFloat(chartData[key][i]) || 0;
            }
          }
          
          newVisibleSums[key] = sum;
        }
      }
    });
    
    console.log("New visible sums:", newVisibleSums);
    
    // Update the visible sums
    setVisibleSumValues(newVisibleSums);
    
    // Use setTimeout to ensure DOM is fully updated
    setTimeout(() => {
      try {
        // Get the chart canvas element
        const chartCanvas = document.querySelector('.chart-wrapper canvas');
        
        if (chartCanvas) {
          console.log("Found chart canvas element");
          
          // Get the Chart.js instance from the canvas
          const chartInstance = Chart.getChart(chartCanvas);
          
          if (chartInstance) {
            console.log("Got chart instance from canvas");
            
            // Disable animations to prevent flashing
            const originalAnimationConfig = { ...chartInstance.options.animation };
            chartInstance.options.animation = false;
            
            // Configure x-axis with our zoom settings
            if (chartInstance.options.scales && chartInstance.options.scales.x) {
              // Save the original configuration
              if (!chartInstance._originalScaleConfig) {
                chartInstance._originalScaleConfig = {
                  x: { ...chartInstance.options.scales.x }
                };
              }
              
              // Configure ticks for better readability
              chartInstance.options.scales.x.ticks = {
                ...chartInstance.options.scales.x.ticks,
                maxTicksLimit: 25,     // Limit to 25 ticks on x-axis
                autoSkip: true,        // Automatically skip labels to fit
                autoSkipPadding: 10,   // Add padding between labels
                maxRotation: 45,       // Maximum rotation for labels
                minRotation: 45        // Minimum rotation for labels
              };
              
              // Set fixed min and max values
              chartInstance.options.scales.x.min = startIndex;
              chartInstance.options.scales.x.max = actualEndIndex;
              
              // Disable the zoom plugin's auto-adjustment
              if (chartInstance.options.plugins && chartInstance.options.plugins.zoom) {
                chartInstance.options.plugins.zoom.limits = {
                  x: {
                    minRange: 1,
                    min: startIndex,
                    max: actualEndIndex
                  }
                };
              }
              
              console.log("Set x-axis limits:", startIndex, actualEndIndex);
            } else {
              console.error("Could not access chart scales");
            }
            
            // Update the chart
            chartInstance.update();
            
            // Store the zoom state in a custom property
            chartInstance._customZoomState = {
              min: startIndex,
              max: actualEndIndex
            };
            
            // Add a hook to maintain zoom after updates
            const originalUpdate = chartInstance.update;
            chartInstance.update = function(...args) {
              const result = originalUpdate.apply(this, args);
              
              // Re-apply zoom after update if we have a custom zoom state
              if (this._customZoomState) {
                this.options.scales.x.min = this._customZoomState.min;
                this.options.scales.x.max = this._customZoomState.max;
                
                // Ensure tick settings are maintained
                this.options.scales.x.ticks = {
                  ...this.options.scales.x.ticks,
                  maxTicksLimit: 25,
                  autoSkip: true,
                  autoSkipPadding: 10
                };
              }
              
              return result;
            };
            
            // Restore animation settings after a delay
            setTimeout(() => {
              chartInstance.options.animation = originalAnimationConfig;
            }, 500);
            
            console.log(`Applied zoom to range: ${startIndex} - ${actualEndIndex}`);
          } else {
            console.error("Could not get chart instance from canvas");
          }
        } else {
          console.error("Could not find chart canvas element");
        }
      } catch (error) {
        console.error("Error applying zoom:", error);
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  // Calculate sum of values for each column
  const calculateSums = useCallback((data) => {
    if (!data) return {};
    
    const sums = {};
    Object.keys(data).forEach(key => {
      if (key !== 'date_time') {
        // Calculate sum of all values for this column
        sums[key] = data[key].reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
      }
    });
    
    return sums;
  }, []);

  // Fetch visualization data from the API
  const fetchVisualizationData = useCallback(async () => {
    if (selectedColumns.length === 0) {
      setChartError("Please select at least one column to visualize");
      return;
    }

    // Filter out date/time columns for y-axis
    const yAxisColumns = selectedColumns.filter(col => 
      !col.toLowerCase().includes('date') && 
      !col.toLowerCase().includes('time') && 
      !col.toLowerCase().includes('month')
    );
    
    if (yAxisColumns.length === 0) {
      setChartError("Please select at least one data column to visualize");
      return;
    }

    try {
      setIsLoading(true);
      setChartError(null);
      
      // Call the API to get visualization data
      const visualizationData = await get_visualization_data(
        pipelineName,
        nodeName,
        yAxisColumns,
        session?.last_run_id
      );
      
      // Process the data for the chart
      if (!visualizationData || Object.keys(visualizationData).length === 0) {
        setChartError("No visualization data returned from the server");
        return;
      }
      
      setChartData(visualizationData);
      
      // Calculate and set the sum values
      const calculatedSums = calculateSums(visualizationData);
      setSumValues(calculatedSums);
      setVisibleSumValues(calculatedSums); // Initially, visible sums are the same as total sums
      
      // Set initial date range and parse date strings to Date objects
      if (visualizationData.date_time && visualizationData.date_time.length > 0) {
        const dateStrings = visualizationData.date_time;
        const parsedDates = dateStrings.map(dateStr => {
          try {
            // Parse date string in format "DD/MM/YYYY HH:MM"
            const [datePart, timePart] = dateStr.split(' ');
            const [day, month, year] = datePart.split('/');
            const [hours, minutes] = timePart ? timePart.split(':') : ['00', '00'];
            
            // JavaScript months are 0-indexed
            return new Date(year, month - 1, day, hours, minutes);
          } catch (error) {
            console.error("Error parsing date:", dateStr, error);
            return new Date(); // Return current date as fallback
          }
        });
        
        console.log("Parsed dates:", parsedDates);
        setDateTimeArray(parsedDates);
        setStartDate(parsedDates[0]);
        setEndDate(parsedDates[parsedDates.length - 1]);
        
        setVisibleDateRange({
          start: visualizationData.date_time[0] || '',
          end: visualizationData.date_time[visualizationData.date_time.length - 1] || ''
        });
      }
    } catch (error) {
      console.error("Error fetching visualization data:", error);
      setChartError(`Error fetching visualization data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [pipelineName, nodeName, selectedColumns, calculateSums]);

  // Validate chart data and prepare it
  const validateChartData = useCallback(() => {
    if (!data || !data.node_output_df || data.node_output_df.length === 0) {
      setChartError("No data available for visualization");
      return false;
    }
    
    if (selectedColumns.length === 0) {
      setChartError("Please select at least one column to visualize");
      return false;
    }
    
    // Check if we have a date/time column
    const columns = Object.keys(data.node_output_df[0]);
    const dateTimeColumns = columns.filter(col => 
      col.toLowerCase().includes('date') || 
      col.toLowerCase().includes('time') || 
      col.toLowerCase().includes('month')
    );
    
    if (dateTimeColumns.length === 0) {
      setChartError("No date or time column found for x-axis");
      return false;
    }
    
    // Ensure the selected columns don't include the date/time columns
    const yAxisColumns = selectedColumns.filter(col => 
      !col.toLowerCase().includes('date') && 
      !col.toLowerCase().includes('time') && 
      !col.toLowerCase().includes('month')
    );
    
    if (yAxisColumns.length === 0) {
      setChartError("Please select at least one data column to visualize");
      return false;
    }
    
    setChartError(null);
    return true;
  }, [data, selectedColumns]);

  // Prepare chart data
  const prepareChartData = useCallback(() => {
    // If we have data from the API, use that
    if (chartData) {
      const labels = chartData.date_time || [];
      
      // Create datasets for each column in the response (except date_time)
      const datasets = Object.keys(chartData)
        .filter(key => key !== 'date_time')
        .map((key, index) => {
          // Generate a color based on the index
          const hue = (index * 137) % 360; // Golden ratio to distribute colors
          const color = `hsl(${hue}, 70%, 60%)`;
          
          return {
            label: key,
            data: chartData[key],
            borderColor: color,
            backgroundColor: `${color}33`, // Add transparency
            tension: 0.2,
            pointRadius: 0, // Remove points to show only the line
            pointHoverRadius: 3, // Show points on hover
          };
        });
      
      return {
        labels,
        datasets,
      };
    }
    
    // Fallback to using the node data directly if API data isn't available
    if (!data || !data.node_output_df || data.node_output_df.length === 0) {
      return null;
    }
    
    // Find all date/time columns for x-axis
    const columns = Object.keys(data.node_output_df[0]);
    const dateColumns = columns.filter(col => col.toLowerCase().includes('date'));
    const timeColumns = columns.filter(col => col.toLowerCase().includes('time'));
    const monthColumns = columns.filter(col => col.toLowerCase().includes('month'));
    
    // Combine date and time columns for x-axis labels
    let labels = [];
    
    if (dateColumns.length > 0 && timeColumns.length > 0) {
      // If we have both date and time, combine them
      labels = data.node_output_df.map(row => `${row[dateColumns[0]]} ${row[timeColumns[0]]}`);
    } else if (dateColumns.length > 0) {
      // If we only have date
      labels = data.node_output_df.map(row => row[dateColumns[0]]);
    } else if (timeColumns.length > 0) {
      // If we only have time
      labels = data.node_output_df.map(row => row[timeColumns[0]]);
    } else if (monthColumns.length > 0) {
      // If we only have month
      labels = data.node_output_df.map(row => row[monthColumns[0]]);
    } else {
      // Fallback to row index if no date/time columns
      labels = data.node_output_df.map((_, index) => `Row ${index + 1}`);
    }
    
    // Create datasets for each selected column (except the date/time columns)
    const datasets = selectedColumns
      .filter(col => 
        !col.toLowerCase().includes('date') && 
        !col.toLowerCase().includes('time') && 
        !col.toLowerCase().includes('month')
      )
      .map((col, index) => {
        // Generate a color based on the index
        const hue = (index * 137) % 360; // Golden ratio to distribute colors
        const color = `hsl(${hue}, 70%, 60%)`;
        
        return {
          label: col,
          data: data.node_output_df.map(row => parseFloat(row[col]) || 0),
          borderColor: color,
          backgroundColor: `${color}33`, // Add transparency
          tension: 0.2,
          pointRadius: 0, // Remove points to show only the line
          pointHoverRadius: 3, // Show points on hover
        };
      });
    
    return {
      labels,
      datasets,
    };
  }, [data, selectedColumns, chartData]); // Removed unnecessary visibleSumValues dependency

  // Function to update sums based on visible data points
  const updateVisibleSums = useCallback((chart) => {
    if (!chartData || !chart) return;
    
    // Get the visible range from the chart
    const { min, max } = chart.scales.x;
    const labels = chartData.date_time || [];
    
    // Get the visible date range
    const startDateStr = labels[Math.ceil(min)] || '';
    const endDateStr = labels[Math.floor(max)] || '';
    
    // Update date pickers to match the current zoom level
    if (dateTimeArray.length > 0) {
      const startIdx = Math.max(0, Math.ceil(min));
      const endIdx = Math.min(dateTimeArray.length - 1, Math.floor(max));
      
      if (startIdx >= 0 && startIdx < dateTimeArray.length) {
        setStartDate(dateTimeArray[startIdx]);
      }
      
      if (endIdx >= 0 && endIdx < dateTimeArray.length) {
        setEndDate(dateTimeArray[endIdx]);
      }
    }
    
    // Calculate new sums for visible data points
    const newVisibleSums = {};
    
    Object.keys(chartData).forEach(key => {
      if (key !== 'date_time' && key !== '_zoomStart' && key !== '_zoomEnd') {
        let sum = 0;
        
        // Check if the data is an array before using forEach
        if (Array.isArray(chartData[key])) {
          // Sum only the visible data points
          chartData[key].forEach((value, index) => {
            // Check if this data point is within the visible range
            if (index >= min && index <= max) {
              sum += parseFloat(value) || 0;
            }
          });
        } else {
          console.warn(`Data for key ${key} is not an array:`, chartData[key]);
        }
        
        newVisibleSums[key] = sum;
      }
    });
    
    // Update the state with new visible sums and date range
    setVisibleSumValues(newVisibleSums);
    setVisibleDateRange({ start: startDateStr, end: endDateStr });
  }, [chartData, dateTimeArray]);

  // Fetch visualization data when selectedColumns change
  useEffect(() => {
    if (selectedColumns.length > 0 && pipelineName && nodeName) {
      fetchVisualizationData();
    }
  }, [selectedColumns, pipelineName, nodeName, fetchVisualizationData]);

  // Validate chart data on mount and when dependencies change
  useEffect(() => {
    validateChartData();
  }, [validateChartData]);

  return (
    <div className="chart-container">
      {isLoading ? (
        <div className="chart-loading">
          <p>Loading visualization data...</p>
        </div>
      ) : chartError ? (
        <div className="chart-error">
          <p>{chartError}</p>
          <p>Please select columns to visualize or switch back to table view.</p>
        </div>
      ) : (
        <>
          {selectedColumns.length > 0 ? (
            <div className="chart-container-with-controls">
              <div className="chart-controls">
                <div className="date-range-picker">
                  <div className="date-picker-container">
                    <label>Start Date:</label>
                    <DatePicker
                      selected={startDate}
                      onChange={date => setStartDate(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      className="date-picker"
                      placeholderText="Select start date"
                    />
                  </div>
                  <div className="date-picker-container">
                    <label>End Date:</label>
                    <DatePicker
                      selected={endDate}
                      onChange={date => setEndDate(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      className="date-picker"
                      placeholderText="Select end date"
                      minDate={startDate}
                    />
                  </div>
                  <button 
                    onClick={applyDateRangeFilter}
                    className="apply-range-btn"
                    disabled={!startDate || !endDate}
                  >
                    Apply Range
                  </button>
                </div>
                <div className="zoom-controls">
                  <button 
                    onClick={resetZoom}
                    className="reset-zoom-btn"
                    title="Reset Zoom"
                  >
                    Reset Zoom
                  </button>
                  <div className="zoom-instructions">
                    <span>Scroll to zoom • Drag to select zoom area • Shift+Drag to pan</span>
                  </div>
                </div>
              </div>
              <div className="chart-wrapper">
                <Line 
                  ref={chartRef}
                  data={prepareChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: `${nodeName} - Data Visualization`,
                      },
                      tooltip: {
                        callbacks: {
                          title: (tooltipItems) => {
                            return `Time: ${tooltipItems[0].label}`;
                          }
                        }
                      },
                      zoom: {
                        limits: {
                          x: {minRange: 1}, // Prevent zooming in too far
                        },
                        pan: {
                          enabled: true,
                          mode: 'x',
                          modifierKey: 'shift', // Hold shift key to pan
                          onPanComplete: function({ chart }) {
                            try {
                              // Update sums based on visible data points
                              updateVisibleSums(chart);
                              
                              // Store the current zoom state
                              if (chart.scales && chart.scales.x) {
                                const min = chart.scales.x.min;
                                const max = chart.scales.x.max;
                                
                                // Save the zoom state
                                chart._customZoomState = { min, max };
                                console.log("Stored pan state:", { min, max });
                              }
                            } catch (error) {
                              console.error("Error in onPanComplete:", error);
                            }
                          }
                        },
                        zoom: {
                          wheel: {
                            enabled: true,
                          },
                          pinch: {
                            enabled: true,
                          },
                          mode: 'x',
                          drag: {
                            enabled: true,
                            backgroundColor: 'rgba(225,225,225,0.3)',
                            borderColor: 'rgba(102,102,102,0.5)',
                            borderWidth: 1,
                          },
                          onZoomComplete: function({ chart }) {
                            try {
                              // Update sums based on visible data points
                              updateVisibleSums(chart);
                              
                              // Store the current zoom state
                              if (chart.scales && chart.scales.x) {
                                const min = chart.scales.x.min;
                                const max = chart.scales.x.max;
                                
                                // Save the zoom state
                                chart._customZoomState = { min, max };
                                console.log("Stored zoom state:", { min, max });
                              }
                            } catch (error) {
                              console.error("Error in onZoomComplete:", error);
                            }
                          }
                        }
                      },
                      // Custom plugin to maintain zoom state
                      persistentZoom: {
                        beforeUpdate: function(chart) {
                          // If we have a custom zoom state, re-apply it
                          if (chart._customZoomState) {
                            const { min, max } = chart._customZoomState;
                            if (chart.options.scales && chart.options.scales.x) {
                              chart.options.scales.x.min = min;
                              chart.options.scales.x.max = max;
                            }
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                          maxTicksLimit: 25, // Limit to 25 ticks on x-axis
                          autoSkip: true,    // Automatically skip labels to fit
                          autoSkipPadding: 10 // Add padding between labels
                        }
                      },
                      y: {
                        beginAtZero: false,
                      }
                    },
                    interaction: {
                      mode: 'nearest',
                      axis: 'x',
                      intersect: false
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="chart-error">
              <p>Please select columns to visualize using the checkboxes in table view.</p>
              <button 
                onClick={onSwitchToTable}
                className="switch-to-table-btn"
              >
                Switch to Table View
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Show data summary and selected columns */}
      {selectedColumns.length > 0 && (
        <div className="visualization-summary">
          {/* Date Range Display */}
          {chartData && (
            <div className="date-range-summary">
              <h5>Date Range:</h5>
              <div className="date-range-info">
                <span className="date-range">
                  {visibleDateRange.start} to {visibleDateRange.end}
                </span>
              </div>
            </div>
          )}
          
          {/* Sum Values Display */}
          {Object.keys(visibleSumValues).length > 0 && (
            <div className="sum-values-summary">
              <h5>Sum Values:</h5>
              <div className="sum-values-grid">
                {Object.entries(visibleSumValues)
                  .sort((a, b) => a[0].localeCompare(b[0])) // Sort alphabetically by column name
                  .map(([key, value]) => (
                    <div key={key} className="sum-value-item">
                      <span className="sum-value-label">{key}:</span>
                      <span className="sum-value-number">{value.toFixed(2)}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
          
          {/* Selected Columns */}
          <div className="selected-columns">
            <h5>Selected Columns:</h5>
            <div className="selected-columns-list">
              {selectedColumns.map(col => (
                <div key={col} className="selected-column-tag">
                  {col}
                  <button 
                    onClick={() => handleColumnSelect(col)}
                    className="remove-column-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            
            {/* Refresh button for visualization data */}
            <button 
              onClick={fetchVisualizationData}
              className="refresh-visualization-btn"
              disabled={isLoading}
            >
              Refresh Visualization
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataVisualization;